import React, { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { OrbitMotif } from "@/components/OrbitMotif";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const MAX_ATTEMPTS = 12; // ~24 seconds at 2s intervals

export default function PaymentSuccess() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling, paid, expired, error
  const attempts = useRef(0);

  useImperativeLoad(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        const ps = r.data?.payment_status;
        const st = r.data?.status;
        if (ps === "paid" || r.data?.granted) {
          setStatus("paid");
          await refresh();
          return;
        }
        if (st === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus("error");
          return;
        }
        setTimeout(poll, 2000);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#05073B] px-6" data-testid="payment-success-page">
      <OrbitMotif className="absolute inset-0 h-screen w-screen" color="#9ACD32" opacity={0.08} />
      <Card className="relative w-full max-w-md border-slate-700 bg-[#0A0C1F] p-10 text-center">
        <div className="mb-6 flex justify-center">
          <Logo size="lg" inverted />
        </div>

        {status === "polling" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#9ACD32]" data-testid="payment-status-polling" />
            <h2 className="mt-6 font-display text-2xl font-bold text-white">
              {t("payment.processing")}
            </h2>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-16 w-16 text-[#9ACD32]" data-testid="payment-status-paid" />
            <h2 className="mt-6 font-display text-3xl font-extrabold text-white">
              {t("payment.success_title")}
            </h2>
            <p className="mt-3 text-slate-300">{t("payment.success_subtitle")}</p>
            <Button
              asChild
              className="lime-glow mt-8 h-12 w-full rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="payment-go-dashboard-btn"
            >
              <Link to="/dashboard">
                {t("payment.go_dashboard")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </>
        )}

        {(status === "expired" || status === "error") && (
          <>
            <XCircle className="mx-auto h-16 w-16 text-red-400" data-testid="payment-status-error" />
            <h2 className="mt-6 font-display text-2xl font-bold text-white">
              {t("payment.cancelled_title")}
            </h2>
            <p className="mt-3 text-slate-300">{t("payment.cancelled_subtitle")}</p>
            <Button
              asChild
              variant="outline"
              className="mt-8 h-12 w-full rounded-full border-slate-700 text-slate-200"
              data-testid="payment-back-pricing-btn"
            >
              <Link to="/pricing">{t("payment.back_pricing")}</Link>
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
